/**
 * Inline info-ikon med popover som viser kort summary.
 * Klikk på "Les mer" åpner <HelpPanel> med full artikkel.
 */
import { useEffect, useRef, useState } from 'react';
import { HelpCircle, Info, Loader2 } from 'lucide-react';
import { getArticle, logView, type HelpArticle, type HelpLanguage } from '../../lib/help';
import HelpPanel from './HelpPanel';

interface Props {
  slug: string;
  language?: HelpLanguage;
  label?: string;
  icon?: 'help' | 'info';
  size?: 'sm' | 'md';
  route?: string;
  className?: string;
}

export default function HelpTooltip({
  slug,
  language = 'nb',
  label,
  icon = 'help',
  size = 'sm',
  route,
  className = ''
}: Props) {
  const [open, setOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const Icon = icon === 'info' ? Info : HelpCircle;
  const iconSize = size === 'md' ? 'w-5 h-5' : 'w-4 h-4';

  useEffect(() => {
    if (!open) return;
    if (article) return;
    setLoading(true);
    setError(null);
    getArticle(slug, language)
      .then(a => {
        setArticle(a);
        logView(slug, 'tooltip', route, language);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, slug, language, article, route]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        buttonRef.current?.contains(e.target as Node) ||
        popoverRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <>
      <span className={`inline-flex items-center gap-1 relative ${className}`}>
        {label && <span className="text-xs text-kf-gray-500">{label}</span>}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(v => !v)}
          className="inline-flex items-center justify-center text-kf-blue-500 hover:text-kf-blue-700 focus:outline-none focus:ring-2 focus:ring-kf-blue-300 rounded-full"
          aria-label={label ? `Hjelp: ${label}` : 'Hjelp'}
          aria-expanded={open}
        >
          <Icon className={iconSize} />
        </button>
        {open && (
          <div
            ref={popoverRef}
            className="absolute z-[8000] top-full left-0 mt-1 w-80 max-w-sm bg-white border border-kf-gray-200 rounded-lg shadow-xl p-3"
            role="tooltip"
          >
            {loading && (
              <div className="flex items-center gap-2 text-xs text-kf-gray-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                Laster...
              </div>
            )}
            {error && (
              <div className="text-xs text-red-700">
                <div className="font-semibold">Fant ikke hjelp</div>
                <div className="text-[11px] mt-0.5 text-red-600">Slug: <code>{slug}</code></div>
              </div>
            )}
            {article && (
              <>
                <div className="text-sm font-semibold text-kf-gray-900 mb-1">{article.title}</div>
                <p className="text-xs text-kf-gray-700 leading-relaxed mb-2">{article.summary}</p>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setPanelOpen(true); }}
                  className="text-xs font-semibold text-kf-blue-700 hover:text-kf-blue-900 underline"
                >
                  Les mer →
                </button>
              </>
            )}
          </div>
        )}
      </span>
      {panelOpen && (
        <HelpPanel
          slug={slug}
          language={language}
          isOpen={panelOpen}
          onClose={() => setPanelOpen(false)}
          route={route}
        />
      )}
    </>
  );
}
